import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';

import { AssistantFeedbackCommentTransition, AssistantFeedbackStatus } from '../../__generated__/globalTypes';

/** The three-state filter on the Feedback tab. `ALL` mixes both statuses. */
export type FeedbackFilter = 'OPEN' | 'RESOLVED' | 'ALL';

export const FEEDBACK_FILTERS: readonly { value: FeedbackFilter; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ALL', label: 'All' },
] as const;

/** Map a filter to the `status` query variable — `null` (no filter) for `ALL`. */
export function filterToStatusVariable(filter: FeedbackFilter): AssistantFeedbackStatus | null {
  if (filter === 'OPEN') {
    return AssistantFeedbackStatus.Open;
  }
  if (filter === 'RESOLVED') {
    return AssistantFeedbackStatus.Resolved;
  }
  return null;
}

/**
 * The status-change action available on a feedback row: an OPEN row can be
 * resolved, a RESOLVED row can be reopened.
 */
export function transitionActionForStatus(status: AssistantFeedbackStatus): {
  label: string;
  transition: 'RESOLVE' | 'REOPEN';
} {
  return status === AssistantFeedbackStatus.Open
    ? { label: 'Resolve', transition: 'RESOLVE' }
    : { label: 'Reopen', transition: 'REOPEN' };
}

/** Badge text for a comment's status-change marker; `null` for a plain comment. */
export function transitionBadgeLabel(transition: AssistantFeedbackCommentTransition | null): string | null {
  if (transition === AssistantFeedbackCommentTransition.Resolved) {
    return 'Resolved';
  }
  if (transition === AssistantFeedbackCommentTransition.Reopened) {
    return 'Reopened';
  }
  return null;
}

/** Compact count label for the Comments column. */
export function commentCountLabel(count: number | null): string {
  if (count === null || count === 0) {
    return '—';
  }
  return String(count);
}

/**
 * Strip space-heavy, redundant fields from a message payload before it goes
 * on the clipboard, leaving the rest untouched:
 *  - assistant `thinking` content blocks keep their text but drop the
 *    multi-kilobyte `thinkingSignature` base64 blob (replay-only, useless for
 *    triage);
 *  - `toolResult` payloads drop `content` — a stringified duplicate of the
 *    structured `details` the operator actually reads.
 * Returns a shallow copy; the original (Apollo-cached) payload is never mutated.
 */
export function sanitizeTranscriptPayload(role: string, payload: unknown): unknown {
  if (payload === null || typeof payload !== 'object') {
    return payload;
  }
  const record = payload as Record<string, unknown>;
  if (role === 'toolResult') {
    const rest = { ...record };
    delete rest.content;
    return rest;
  }
  if (role === 'assistant' && Array.isArray(record.content)) {
    return {
      ...record,
      content: record.content.map((block) => {
        if (block !== null && typeof block === 'object' && 'thinkingSignature' in block) {
          const blockRest = { ...(block as Record<string, unknown>) };
          delete blockRest.thinkingSignature;
          return blockRest;
        }
        return block;
      }),
    };
  }
  return payload;
}

/** One turn's worth of transcript messages, grouped for the clipboard export. */
export interface IFeedbackTranscriptTurn<M> {
  turnId: string;
  /** True for the turn the feedback targets (the others are preceding context). */
  isTaggedTurn: boolean;
  messages: M[];
}

/** The conversation window copied alongside a feedback summary. */
export interface IFeedbackTranscript<M> {
  taggedTurnId: string;
  turns: IFeedbackTranscriptTurn<M>[];
}

/**
 * Group a flat, sequence-ordered list of messages into turns, preserving
 * first-seen (oldest→newest) order, and flag the turn the feedback targets.
 * Generic over the message shape — the builder only needs `turnId` to group;
 * every other field passes through untouched into the JSON export.
 */
export function buildFeedbackTranscript<M extends { turnId: string }>(
  messages: readonly M[],
  taggedTurnId: string
): IFeedbackTranscript<M> {
  const turns: IFeedbackTranscriptTurn<M>[] = [];
  const byTurnId = new Map<string, IFeedbackTranscriptTurn<M>>();
  for (const message of messages) {
    let turn = byTurnId.get(message.turnId);
    if (!turn) {
      turn = { turnId: message.turnId, isTaggedTurn: message.turnId === taggedTurnId, messages: [] };
      byTurnId.set(message.turnId, turn);
      turns.push(turn);
    }
    turn.messages.push(message);
  }
  return { taggedTurnId, turns };
}

export interface IFeedbackClipboardInput {
  submitterEmail: string | null;
  userId: string;
  /** ISO timestamp of submission. */
  createdAt: string;
  body: string;
  /** Absolute URL back to this feedback row on MOPS. */
  deeplinkUrl: string;
  /**
   * Conversation window (tagged turn + preceding turns) appended as a fenced
   * JSON block. Omitted / null when the transcript fetch failed or returned
   * nothing — the summary still copies on its own.
   */
  transcript?: IFeedbackTranscript<unknown> | null;
}

/** Render a submission instant in Pacific time for the clipboard export, e.g. `Jun 1, 2026, 1:30 PM PDT`. */
export function formatFeedbackDatePacific(createdAt: string): string {
  return formatPacificDateTime(createdAt);
}

/** Build the markdown block copied to the clipboard by the Copy action. */
export function formatFeedbackForClipboard(input: IFeedbackClipboardInput): string {
  const submittedBy = input.submitterEmail ?? input.userId;
  const lines = [
    `**Submitted by**: ${submittedBy}`,
    `**Date**: ${formatFeedbackDatePacific(input.createdAt)}`,
    '',
    '**Feedback**:',
    input.body,
    '',
    '**Link**:',
    input.deeplinkUrl,
  ];
  if (input.transcript) {
    lines.push('', '**Transcript**:', '```json', JSON.stringify(input.transcript, null, 2), '```');
  }
  return lines.join('\n');
}
